import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Integración con Google Identity Services (GIS) para obtener un `id_token`.
 *
 * Por qué el botón lo dibuja Google y no nosotros: GIS entrega el id_token únicamente a
 * través de `google.accounts.id`, que renderiza su propio botón dentro de un iframe. No hay
 * API soportada para disparar el flujo de id_token desde un botón propio, y la API de
 * autorización (`initTokenClient` / `initCodeClient`) devuelve access_token o code, que el
 * backend no acepta. Por eso el botón custom que había antes se reemplazó por este.
 *
 * Tampoco se usa One Tap: `prompt()` no se llama a propósito, para no abrir un diálogo
 * flotante sobre el formulario sin que el usuario lo haya pedido.
 */

const GSI_SCRIPT_ID = 'google-identity-services';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

/** Tope duro de `renderButton`: anchos mayores se ignoran. */
const MAX_BUTTON_WIDTH = 400;
const MIN_BUTTON_WIDTH = 200;

/** Promesa compartida: el script se descarga una sola vez por sesión de página. */
let scriptPromise = null;

function loadGsiScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const onError = () => {
      // Se limpia la promesa para permitir reintentar en una visita posterior: si el
      // usuario recupera la conexión, no queremos dejar el botón muerto para siempre.
      scriptPromise = null;

      // Y se remueve el <script> muerto. Sin esto el reintento es peor que no reintentar:
      // el elemento sigue en el head, la rama `existing` de abajo le engancha listeners
      // 'load'/'error' que un <script> ya no vuelve a emitir (los dispara una sola vez, y
      // el navegador no re-solicita el src de un tag ya presente), y la promesa queda
      // pendiente para siempre cacheada en `scriptPromise`. El hook se congelaría en
      // 'loading' en vez de pasar a 'error', dejando el separador sobre un hueco vacío sin
      // el mensaje que explica por qué no hay botón.
      document.getElementById(GSI_SCRIPT_ID)?.remove();

      reject(new Error('No se pudo cargar Google Identity Services'));
    };

    const existing = document.getElementById(GSI_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', onError);
      return;
    }

    const script = document.createElement('script');
    script.id = GSI_SCRIPT_ID;
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = onError;
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * @param {Object} options
 * @param {string} [options.clientId] OAuth Client ID de tipo "Web application". Sin él el
 *   hook queda en estado `disabled` y la vista no debe renderizar el botón.
 * @param {(idToken: string) => void} options.onCredential recibe el id_token de Google.
 * @param {boolean} [options.isDark] tema actual, para que el botón no quede blanco sobre
 *   fondo oscuro. El iframe de Google no hereda los tokens de color de la app.
 * @param {string} [options.locale] idioma del texto del botón.
 * @returns {{ containerRef: React.RefObject, status: 'disabled'|'loading'|'ready'|'error' }}
 */
export function useGoogleSignIn({ clientId, onCredential, isDark = false, locale }) {
  const containerRef = useRef(null);
  const callbackRef = useRef(onCredential);
  const lastWidthRef = useRef(0);
  const [status, setStatus] = useState(clientId ? 'loading' : 'disabled');

  // El callback se guarda en una ref para que un cambio de identidad de la función (cada
  // render del padre) no fuerce a reinicializar GIS ni a redibujar el botón.
  useEffect(() => {
    callbackRef.current = onCredential;
  }, [onCredential]);

  /**
   * @param {boolean} force redibuja aunque el ancho no haya cambiado (cambio de tema/idioma).
   */
  const renderButton = useCallback(
    (force = false) => {
      const el = containerRef.current;
      if (!el || !window.google?.accounts?.id) return;

      // `renderButton` no produce un botón fluido: hay que darle un ancho en píxeles y
      // volver a dibujarlo cuando el contenedor cambia de tamaño. La guarda por ancho evita
      // redibujar en cada resize intermedio (y con él, el parpadeo del iframe).
      //
      // Se cae al ancho del padre si el contenedor mide 0: pasa cuando todavía no tiene
      // contenido y su ancho depende de él, y sin este fallback el botón sale al mínimo.
      const available =
        Math.floor(el.getBoundingClientRect().width) ||
        Math.floor(el.parentElement?.getBoundingClientRect().width || 0);
      const width = Math.min(Math.max(available || MIN_BUTTON_WIDTH, MIN_BUTTON_WIDTH), MAX_BUTTON_WIDTH);

      if (!force && width === lastWidthRef.current) return;
      lastWidthRef.current = width;

      el.innerHTML = '';
      window.google.accounts.id.renderButton(el, {
        type: 'standard',
        theme: isDark ? 'filled_black' : 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: String(width),
        ...(locale ? { locale } : {}),
      });
    },
    [isDark, locale],
  );

  // Carga e inicialización: depende solo del clientId, para no reinicializar GIS cada vez
  // que el usuario alterna el tema.
  useEffect(() => {
    if (!clientId) {
      setStatus('disabled');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    loadGsiScript()
      .then(() => {
        if (cancelled) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            // `credential` es el id_token (JWT firmado por Google) que valida el backend.
            if (response?.credential) callbackRef.current?.(response.credential);
          },
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        setStatus('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('[GoogleSignIn] No se pudo inicializar Google Identity Services:', error);
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Dibujado del botón: al quedar listo, al cambiar tema/idioma y al cambiar el ancho de la
  // ventana. Se escucha `resize` de window y no un ResizeObserver sobre el contenedor
  // porque el propio redibujado altera el contenido del contenedor y se retroalimentaría.
  useEffect(() => {
    if (status !== 'ready') return undefined;

    renderButton(true);

    const onResize = () => renderButton();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [status, renderButton]);

  return { containerRef, status };
}

export default useGoogleSignIn;
